/**
 * lib/pdfHeader.ts
 * Shared header band + company block so every document PDF (invoice,
 * statement, credit note) renders an identical letterhead.
 */
import type { jsPDF } from 'jspdf';
import type { CompanySettings } from '@/types';
import { drawDocGlyph, drawWatermark, brandName, type DocKind } from './pdfIcons';

export const PDF_ORANGE: [number, number, number] = [255, 107, 53];
export const PDF_CHAR: [number, number, number] = [34, 39, 46];
export const PDF_GRAY: [number, number, number] = [120, 128, 138];
export const PDF_LINE: [number, number, number] = [228, 230, 233];
export const PAGE_W = 210;
export const MARGIN = 15;

export function fmtDate(iso?: string): string {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

/** Inset orange band with chevron, wordmark, and a right-aligned title. */
export function drawBand(doc: jsPDF, company: CompanySettings, title: string, kind: DocKind = 'invoice'): void {
  const W = PAGE_W;
  const M = MARGIN;
  const bandW = W - 2 * M;
  const bandY = 12;
  const bandH = 26;
  doc.setFillColor(...PDF_ORANGE);
  doc.rect(M, bandY, bandW, bandH, 'F');
  const cy = bandY + bandH / 2;
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(1.4);
  doc.setLineCap('round');
  doc.setLineJoin('round');
  const cW = 3.4;
  const chX = M + 8;
  doc.line(chX, cy - 3.4, chX + cW, cy);
  doc.line(chX + cW, cy, chX, cy + 3.4);
  const wordX = chX + cW + 3;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(brandName(company), wordX, cy - 0.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('PROPRIETARY LIMITED', wordX, cy + 4.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title, M + bandW - 6, cy + 1.5, { align: 'right' });
  drawDocGlyph(doc, kind, M + bandW / 2, cy, 13, [255, 255, 255]);
  drawWatermark(doc, kind);
}

/** Company contact block under the band. Returns the next y. */
export function drawCompanyBlock(doc: jsPDF, company: CompanySettings, y = 48): number {
  doc.setTextColor(...PDF_GRAY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  const lines = [
    company.physicalAddress,
    company.email,
    company.phones?.join('  ·  '),
    company.registrationNumber ? `Reg: ${company.registrationNumber}` : '',
    company.tin ? `TIN: ${company.tin}` : '',
  ].filter(Boolean) as string[];
  lines.forEach((l) => {
    doc.text(l, MARGIN, y);
    y += 5;
  });
  return y;
}

/**
 * "Banking details" block. Font is 2pt larger than the old inline
 * version and the account number is bold. Returns the next y.
 */
export function drawBankingDetails(
  doc: jsPDF,
  bank: NonNullable<CompanySettings['bank']>,
  x = MARGIN,
  y = 235,
): number {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PDF_GRAY);
  doc.text('BANKING DETAILS', x, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...PDF_CHAR);

  doc.text(`${bank.bankName}${bank.branch ? ` — ${bank.branch}` : ''}`, x, y);
  y += 5.5;
  doc.text(`Account name: ${bank.accountName}`, x, y);
  y += 5.5;

  // Account number — label normal, number bold.
  const label = 'Account number: ';
  doc.setFont('helvetica', 'normal');
  doc.text(label, x, y);
  const lw = doc.getTextWidth(label);
  doc.setFont('helvetica', 'bold');
  doc.text(bank.accountNumber, x + lw, y);
  doc.setFont('helvetica', 'normal');
  y += 5.5;

  const extra = [bank.branchCode ? `Branch code: ${bank.branchCode}` : '', bank.swift ? `SWIFT: ${bank.swift}` : '']
    .filter(Boolean)
    .join('     ·     ');
  if (extra) {
    doc.text(extra, x, y);
    y += 5.5;
  }
  return y;
}
